import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag, LogOut, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { scrollToHash } from "@/lib/scroll";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";

const links = [
  { label: "Compounds", hash: "#products", testid: "nav-link-products" },
  { label: "Quality", hash: "#quality", testid: "nav-link-quality" },
  { label: "Lab Tests", hash: "#lab-reports", testid: "nav-link-lab-reports" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const { count, setIsOpen } = useCart();
  const { user, openAuth, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-[#E4E4E7]" : "bg-white/70 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
        <a href="#" data-testid="nav-logo" onClick={(e) => { e.preventDefault(); window.__lenis?.scrollTo(0); }} className="flex items-center gap-3 text-[#09090B]">
          <Logo className="w-7 h-7" />
          <span className="font-display font-bold tracking-[0.18em] text-sm uppercase">Aetheris Labs</span>
        </a>
        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.hash}
              href={l.hash}
              data-testid={l.testid}
              onClick={scrollToHash(l.hash)}
              className="font-mono-clinical text-xs tracking-[0.2em] uppercase text-[#71717A] hover:text-[#6D28D9] transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          {user === false && (
            <button
              onClick={() => openAuth("register")}
              data-testid="nav-signin-button"
              className="font-mono-clinical text-xs tracking-[0.15em] uppercase px-5 py-2.5 border border-[#D4D4D8] text-[#52525B] hover:border-[#6D28D9]/60 hover:text-[#09090B] transition-colors duration-300"
            >
              Sign In
            </button>
          )}
          {user && (
            <div className="hidden sm:flex items-center gap-2" data-testid="nav-account-chip">
              <span className="flex items-center gap-1.5 font-mono-clinical text-[11px] tracking-[0.1em] uppercase text-[#71717A] px-3 py-2.5 border border-[#E4E4E7]">
                <UserRound className="w-3.5 h-3.5 text-[#6D28D9]" strokeWidth={1.5} />
                {user.name}
              </span>
              <button
                onClick={async () => { await logout(); toast.success("Signed out"); }}
                data-testid="nav-logout-button"
                title="Sign out"
                className="p-2.5 border border-[#E4E4E7] text-[#71717A] hover:text-red-400 hover:border-red-400/40 transition-colors duration-300"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          )}
          <button
            onClick={() => setIsOpen(true)}
            data-testid="nav-cart-button"
            className="relative flex items-center gap-2 font-mono-clinical text-xs tracking-[0.15em] uppercase px-5 py-2.5 border border-[#6D28D9]/50 text-[#6D28D9] hover:bg-[#6D28D9] hover:text-white transition-colors duration-300"
          >
            <ShoppingBag className="w-4 h-4" />
            Cart
            {count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-2 -right-2 w-5 h-5 bg-[#6D28D9] text-white text-[10px] font-bold flex items-center justify-center rounded-full">
                {count}
              </span>
            )}
          </button>
        </div>
      </div>
    </motion.header>
  );
};
